import type { ExtendedDoc, ExtendedOp } from "../shared/types/worksheet";

import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";

/**
 * Submission tracking system for silent failure detection.
 *
 * This system tracks ShareDB operation submissions through their complete lifecycle
 * to detect silent failures where operations appear to succeed but don't actually
 * make it to the server..
 *
 * ## ShareDB Operation Lifecycle
 *
 * 1. **Submission**: User calls `submitOp()` on a ShareDB document
 * 2. **Queuing**: Operation is added to `pendingOps` queue (or composed into existing operation)
 * 3. **Local Application**: Operation is applied to local document for optimistic updates
 * 4. **Sending**: When no inflight operation exists, the operation is sent to server
 * 5. **Acknowledgment**: Server processes and sends back acknowledgment
 *
 * ## Operation Composition Challenge
 *
 * ShareDB can combine multiple operations into a single operation for efficiency.
 * When this happens, our original operation becomes part of a composed operation,
 * making individual tracking difficult. We detect composition by checking if our
 * operation appears in the pending ops in the "before op batch" event - if not,
 * it was composed and is now part of another operation.
 *
 * ## Tracking Events
 *
 * - **"before op batch"**: Before operations are combined into a batch
 * - **"before op"**: Before operation is applied to local document
 * - **"op"**: After operation is applied to local document
 * - **"send"**: When operation is sent to server via connection
 * - **"receive"**: When server acknowledgment is received
 *
 * ## Silent Failure Detection
 *
 * We set a 30-second timeout on each operation. If no acknowledgment is received
 * within this time, we log detailed diagnostics about where the operation got stuck.
 */
export interface SubmissionTracker {
  id: number;
  docId: string;
  operationId: string;
  combinedOpIds: string[];
  startTime: number;
  timeoutId: NodeJS.Timeout | null;
  currentStage: string;
  doc: ExtendedDoc;
  seq?: number;
  src?: string | null;
}

const SUBMISSION_TRACKING_TIMEOUT_MS = 60000; // 1 minute

const submissionTrackers = {} as Record<string, SubmissionTracker>;
let nextTrackerId = 1;

export const createSubmissionTracker = (
  doc: ExtendedDoc,
  operationId: string,
): SubmissionTracker => {
  nextTrackerId += 1;
  const tracker: SubmissionTracker = {
    id: nextTrackerId,
    docId: doc.id,
    operationId,
    combinedOpIds: [],
    startTime: Date.now(),
    timeoutId: null,
    currentStage: "submitted",
    doc,
  };

  // Set timeout to track op submission
  tracker.timeoutId = setTimeout(() => {
    const age = Date.now() - tracker.startTime;
    rollbarAndLogError("ShareDB operation timeout - silent failure detected", {
      trackerId: tracker.id,
      docId: tracker.docId,
      operationId: tracker.operationId,
      age,
      stage: tracker.currentStage,
      connectionState: doc.connection?.state,
      canSend: doc.connection?.canSend,
      seq: tracker.seq,
      src: tracker.src,
      paused: doc.paused,
      hasInflightOp: !!doc.inflightOp,
      inflightOpAge: doc.inflightOp?.sentAt ? Date.now() - doc.inflightOp.sentAt : null,
      inflightOpRetries: doc.inflightOp?.retries || 0,
    });

    delete submissionTrackers[operationId];
  }, SUBMISSION_TRACKING_TIMEOUT_MS);

  submissionTrackers[operationId] = tracker;
  return tracker;
};

export const getOperationId = (op: ExtendedOp) => {
  if (op.operationId) {
    return op.operationId;
  }

  if (op.op) {
    return op.op.find((o) => o.operationId)?.operationId;
  }
};

export const updateSubmissionTracker = (
  operationId: string,
  pendingOps: ExtendedOp[],
  stage: string,
) => {
  // If the op is part of the pending ops we should continue to track it
  if (pendingOps.some((p) => getOperationId(p) === operationId)) {
    const tracker = getSubmissionTracker(operationId);
    if (!tracker) return;

    tracker.currentStage = stage;
  } else {
    // It's possible the op has already been sent.  If it's sent we want to track it
    // but otherwise it means it's been combined with another op and we don't need to
    // track it anymore
    const tracker = getSubmissionTracker(operationId);
    if (tracker && !tracker.seq) {
      logInfoWithDebugFlag("debug_sharedb_silent_failures", `Op has been combined,`, {
        pendingOps,
        operationId,
      });

      // If the op was composed into another op, add the op id to that tracker
      const last = pendingOps[pendingOps.length - 1];
      if (last) {
        const composedOpId = getOperationId(last);
        if (composedOpId) {
          const composedTracker = getSubmissionTracker(composedOpId);
          if (composedTracker && !composedTracker.combinedOpIds.includes(operationId)) {
            composedTracker.combinedOpIds?.push(operationId);
          }
        }
      }

      clearSubmissionTrackerById(operationId);
    }
  }
};

const setTrackerAcknowledgeInfo = (
  tracker: SubmissionTracker,
  seq: number,
  src: string | undefined | null,
) => {
  /* eslint-disable no-param-reassign */
  tracker.seq = seq;
  tracker.src = src;
  tracker.currentStage = "sent";
  /* eslint-enable no-param-reassign */
};

export const updateSubmissionTrackersAfterSend = (
  seq: number,
  src: string,
  newSrc?: string | null,
) => {
  const currentDoc = Object.values(submissionTrackers).find(
    (tkr) => tkr.doc.inflightOp?.seq === seq && tkr.doc.inflightOp?.src === src,
  )?.doc;
  if (!currentDoc) return;

  // When sharedb sends something from a submitOp it also sets
  // the inflightOp on the document.  We use this to continue tracking
  // the op as it should tell us which op was sent
  const inflightOp = currentDoc?.inflightOp;

  const opIds: string[] = [];
  if (inflightOp?.operationId) {
    opIds.push(inflightOp.operationId);
  }

  if (inflightOp?.op) {
    inflightOp.op.forEach((op) => {
      if (op.operationId) {
        opIds.push(op.operationId);
      }
    });
  }

  opIds.forEach((opId) => {
    const tracker = getSubmissionTracker(opId);
    if (tracker) {
      logInfoWithDebugFlag("debug_sharedb_silent_failures", `Op submission has been sent,`, {
        operationId: opId,
        seq,
        inflightOp,
      });

      // We may not have a src yet so we take the connection id as it gets set after sending
      setTrackerAcknowledgeInfo(tracker, seq, src || newSrc);
    }
  });
};

export const clearSubmissionTrackers = (seq: number, src: string) => {
  const trackers = getTrackersBySeqAndSrc(seq, src);

  trackers.forEach((tracker) => {
    clearSubmissionTrackerById(tracker.operationId);
  });
};

export const clearSubmissionTrackerById = (operationId: string) => {
  const tracker = getSubmissionTracker(operationId);

  if (!tracker) return;

  logInfoWithDebugFlag("debug_sharedb_silent_failures", `Clearing tracker,`, {
    operationId,
    seq: tracker.seq,
    stage: tracker.currentStage,
  });

  if (tracker.timeoutId) {
    clearTimeout(tracker.timeoutId);
  }

  delete submissionTrackers[operationId];
};

export const getSubmissionTracker = (operationId: string): SubmissionTracker | undefined => {
  let tracker: SubmissionTracker | undefined = submissionTrackers[operationId];

  if (!tracker) {
    const trackers = getTrackers();
    tracker = trackers.find((tkr) => tkr.combinedOpIds.includes(operationId));
  }

  return tracker;
};

const getTrackers = (): SubmissionTracker[] => Object.values(submissionTrackers);

const getTrackersBySeqAndSrc = (seq: number, src: string | null): SubmissionTracker[] => {
  const trackers = getTrackers();

  return trackers.filter((tkr) => tkr.seq === seq && tkr.src === src);
};
